import { ReactNode } from 'react';
import { FiAlertTriangle } from 'react-icons/fi';

import { convertEmojis } from 'utils/helpers';
import { BehaviorLabel } from 'utils/strings';

import { Recognition } from '../recognition';
import { COIN_TIERS } from '../recognition-points';
import { ColleagueAvatar, colleaguePlacement } from './colleague';
import { BEHAVIOR_ART, COIN_ART } from './recognition-art';
import { StepHeading, StepPanel } from './step-layout';

const HUMANOGRAMA_URL = 'https://humanograma.intranet.bb.com.br';

/** Cartão do resumo. O rótulo é só para o leitor de tela, que sem ele ouviria "Colaboração #VALEU 60 pontos" de uma vez. */
function SummaryTile({ art, label, children }: { art: string; label: string; children: ReactNode }) {
  return (
    <p className="m-0 flex min-w-0 flex-col items-center gap-2 rounded-[10px] border-2 border-brand-pink px-2 py-3 text-center sm:px-3 sm:py-3.5">
      <img src={art} alt="" className="h-11 w-11 object-contain sm:h-[54px] sm:w-[54px]" />
      <span className="screen-reader-text">{label}: </span>
      {children}
    </p>
  );
}

/** Mensagem do reconhecimento, com os emojis convertidos em imagem como nas demais telas. */
export function RecognitionMessage({ message, className = '' }: { message: string; className?: string }) {
  return (
    <div
      className={`break-words text-[13px] leading-[1.6] text-content [&_p]:m-0 ${className}`}
      dangerouslySetInnerHTML={{ __html: convertEmojis(message) }}
    />
  );
}

/** Etapa 5: revisão antes do envio (REC-43), com o aviso de cancelamento no fim (AJ3-25). */
export function ReviewStep({ recognition }: { recognition: Recognition }) {
  const { target, behavior, coin, points, message } = recognition;
  const placement = colleaguePlacement(target);

  return (
    <StepPanel>
      <StepHeading hidden>Revise o reconhecimento antes de enviar</StepHeading>

      <div className="flex flex-col gap-3.5 rounded-jj-lg bg-brand-white px-4 py-[18px] sm:px-5">
        <p className="m-0 text-[13px] text-content">Você enviou para:</p>

        <p className="m-0 flex flex-wrap items-center gap-x-2.5 gap-y-1">
          <ColleagueAvatar person={target} />
          <a
            href={`${HUMANOGRAMA_URL}/${target._id}`}
            target="_blank"
            rel="noreferrer"
            className="font-display text-sm font-bold text-content underline hover:text-brand-blue-dark"
          >
            {target.name}
            <span className="screen-reader-text"> (abre o Humanograma em nova aba)</span>
          </a>
          <span className="text-xs text-content-soft">({target._id})</span>
          {placement && <span className="text-xs text-brand-blue-dark">- {placement}</span>}
        </p>

        <div className="grid grid-cols-2 gap-2.5 sm:gap-3.5">
          <SummaryTile label="Comportamento" art={BEHAVIOR_ART[behavior]}>
            <span className="text-xs text-content">{BehaviorLabel[behavior]}</span>
          </SummaryTile>

          <SummaryTile label="Tipo" art={COIN_ART[coin]}>
            <span className="font-display text-xs font-bold text-content">{COIN_TIERS[coin].label}</span>
            <span className="whitespace-nowrap rounded-jj-pill bg-brand-purple-light px-3 py-[5px] text-[11px] font-semibold text-content">
              {points} pontos
            </span>
          </SummaryTile>
        </div>

        <div>
          <p className="m-0 mb-1 font-display text-[13px] font-bold text-brand-blue-dark">Reconhecimento enviado:</p>
          <RecognitionMessage message={message} />
        </div>
      </div>

      {/* AJ3-25: o aviso fecha o quadro rosa, logo antes dos botões. */}
      <p className="m-0 flex items-start gap-3 text-[13px] leading-[1.6] text-content [text-wrap:pretty]">
        <FiAlertTriangle aria-hidden="true" size={26} strokeWidth={1.9} className="shrink-0 text-brand-blue-dark" />
        Depois de enviar o reconhecimento, você ainda poderá cancelar nos próximos 15 minutos. Além disso, é possível editar ou
        solicitar a devolução ao destinatário até 10 dias após o envio (seus pontos só serão estornados até o final do mês do
        envio). Deseja enviar esse reconhecimento?
      </p>
    </StepPanel>
  );
}
